import { useCallback, useMemo, useRef } from 'react'
import { Fab, styled } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { Clear } from '@mui/icons-material'
import SearchHeader from '../../SearchHeader'
import { SingleSelectSearchController } from '../../SingleSelectTable'
import { AdminRow } from '../types'

const RemoveFab = styled(Fab)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,
  border: `${theme.spacing(0.25)} solid`,
  '&:hover': {
    background: theme.palette.grey[200],
  },
}))

interface UseAdminsTableColumnsArgs {
  search: SingleSelectSearchController
  onRemoveAdmin: (userId: string) => void
  isRemoveAdminLoading: boolean
  emailLabel: string
}

export const useAdminsTableColumns = ({
  search,
  onRemoveAdmin,
  isRemoveAdminLoading,
  emailLabel,
}: UseAdminsTableColumnsArgs) => {
  const searchValueRef = useRef(search.value)
  const searchActiveRef = useRef(search.active)
  const searchInputRef = useRef<HTMLInputElement>(null)

  searchValueRef.current = search.value
  searchActiveRef.current = search.active

  const handleToggleSearch = useCallback(
    (action?: 'toggle' | 'off') => {
      search.toggle(action, () => {
        window.setTimeout(() => {
          searchInputRef.current?.focus()
        }, 100)
      })
    },
    [search]
  )

  return useMemo<GridColDef<AdminRow>[]>(
    () => [
      {
        field: 'email',
        headerName: emailLabel,
        flex: 1,
        minWidth: 250,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <SearchHeader
            active={searchActiveRef.current}
            label={emailLabel}
            value={searchValueRef.current}
            inputRef={searchInputRef}
            onToggle={handleToggleSearch}
            onChange={search.setValue}
            onClear={search.clear}
          />
        ),
        valueGetter: params => params.row.email || '-',
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        align: 'right',
        disableColumnMenu: true,
        renderCell: params =>
          params.row.viewerCanRemove ? (
            <RemoveFab
              color="primary"
              size="small"
              onClick={() => {
                if (!params.row.viewerCanRemove) return

                onRemoveAdmin(params.row.id)
              }}
              disabled={isRemoveAdminLoading}
            >
              <Clear />
            </RemoveFab>
          ) : null,
      },
    ],
    [
      handleToggleSearch,
      isRemoveAdminLoading,
      emailLabel,
      onRemoveAdmin,
      search.clear,
      search.setValue,
    ]
  )
}
